import React from "react";
import { useState } from "react";
import {
    Drawer,
    Box,
    Typography,
    IconButton,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    CircularProgress,
    Grid,
    Chip,
    Tooltip,
    Paper,
    Pagination,
} from "@mui/material";
import ExpandMore from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import ErrorOutlineOutlined from "@mui/icons-material/ErrorOutlineOutlined";
import moment from "moment";
import PlayArrow from "@mui/icons-material/PlayArrow";
import PauseCircleOutline from "@mui/icons-material/PauseCircleOutline";
import CancelOutlined from "@mui/icons-material/CancelOutlined";
import { alpha } from "@mui/material/styles";
import { API_SERVICE_BACKEND } from "../../../../config";

export default function UploadListView({
    open,
    onClose,
    data = [],
    loading = false,
    onTogglePause,
    page = 1,
    totalPages = 1,
    onPageChange,
}) {


    const [actionLoading, setActionLoading] = useState({});
    const [errorData, setErrorData] = useState({});
    const [errorLoading, setErrorLoading] = useState({});
    const [errorPage, setErrorPage] = useState({});
    const [hasMoreErrors, setHasMoreErrors] = useState({});

    const getJobStatusProps = (status) => {
        if (status === "FAILED")
            return {
                color: "#f44336",
                tooltip: "Failed",
                icon: <ErrorOutlineOutlined />,
            };
        if (status === "COMPLETED")
            return { color: "#66BB6A", tooltip: "Completed", icon: <PlayArrow /> };
        if (status === "PAUSED")
            return {
                color: "#FFA726",
                tooltip: "Paused",
                icon: <PauseCircleOutline />,
            };
        return {
            color: "#1976d2",
            tooltip: "In Progress",
            icon: <PauseCircleOutline />,
        };
    };

    const fetchErrors = async (id, page = 1, limit = 10) => {
        const token = localStorage.getItem("token");

        const res = await fetch(
            `${API_SERVICE_BACKEND}/mailboxes/bulkMailboxAdd/list/${id}/errors?page=${page}&limit=${limit}`,
            {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                    Authorization: `Bearer ${token}`,
                },
            }
        );

        if (!res.ok) throw new Error("Failed to fetch errors");

        return await res.json();
    };

    const loadErrors = async (fileId, page = 1) => {
        try {
            setErrorLoading((prev) => ({ ...prev, [fileId]: true }));

            const res = await fetchErrors(fileId, page);

            // const newErrors = res.data || [];
            const newErrorsRaw = res.data;

            // normalize to array 
            const newErrors = Array.isArray(newErrorsRaw)
                ? newErrorsRaw
                : newErrorsRaw
                    ? [newErrorsRaw]
                    : [];

            setErrorData((prev) => ({
                ...prev,
                [fileId]: page === 1
                    ? newErrors
                    : [...(prev[fileId] || []), ...newErrors],
            }));

            setErrorPage((prev) => ({
                ...prev,
                [fileId]: page,
            }));

            // if returned 
            setHasMoreErrors((prev) => ({
                ...prev,
                [fileId]: newErrors.length === 10,
            }));

        } catch (err) {
            console.error(err);
        } finally {
            setErrorLoading((prev) => ({ ...prev, [fileId]: false }));
        }
    };


    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            sx={{ zIndex: 1300 }}
            PaperProps={{
                sx: {
                    width: "38vw",
                    maxWidth: 540,
                    minWidth: 340,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    //overflow: "hidden",
                },
                className: "glass-popup",
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    p: 2,
                    borderBottom: "1px solid #eee",
                }}
            >
                <Typography sx={{ fontWeight: 600, fontSize: 18 }}>
                    Uploaded Files
                </Typography>
                <IconButton onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </Box>

            {/* Content */}
            {/* <Box sx={{ p: 2, overflow: "auto", height: "100%" }}> */}
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    minHeight: 0,
                }}
            >

                <Box
                    sx={{
                        flex: 1,
                        overflowY: "auto",
                        p: 2,
                        minHeight: 0,
                    }}
                >
                    {loading ? (
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                alignItems: "center",
                                height: "100%",
                                gap: 2,
                            }}
                        >
                            <CircularProgress size={40} />
                            <Typography variant="body2" color="text.secondary">
                                Loading Files...
                            </Typography>
                        </Box>
                    ) : !loading && data.length === 0 ? (
                        <Typography align="center" color="text.secondary">
                            No uploaded files found
                        </Typography>
                    ) : (
                        data.map((file, index) => {
                           
                            const { color, tooltip, icon } = getJobStatusProps(
                                file?.generation?.status
                            );
                            const success =
                                Number(file?.generation?.opState?.generated) || 0;

                            const failed =
                                Number(file?.generation?.opState?.failed) || 0;

                            const duplicates =
                                Number(file?.generation?.opState?.duplicates) || 0;


                            // const total = file?.settings?.maxSize || 0;
                            // const total =
                            //     (Number(file?.generation?.opState?.generated) || 0) +
                            //     (Number(file?.generation?.opState?.failed) || 0) +
                            //     (Number(file?.generation?.opState?.duplicates) || 0);
                            // const percent =
                            //     total > 0 ? Math.min((success / total) * 100, 100) : 0;
                            const total = file?.settings?.maxSize || 0;
                            const processed = success + failed + duplicates;
                            const percent =
                                file?.generation?.status === "COMPLETED"
                                    ? 100
                                    : total > 0
                                        ? Math.min((processed / total) * 100, 100)
                                        : 0;
                            const hasErrors = failed > 0;


                            return (
                                <Accordion
                                    key={file._id || index}
                                    sx={{
                                        mb: 2,
                                        borderRadius: "12px !important",
                                        border: "1px solid #e0e0e0",
                                        "&:before": { display: "none" },
                                    }}
                                    className="glass-wrapper-50"
                                >
                                    {/* SUMMARY */}
                                    <AccordionSummary expandIcon={<ExpandMore />}
                                        sx={{
                                            minHeight: "72px",
                                            borderRadius: "12px",
                                            "&.Mui-expanded": {
                                                borderBottomLeftRadius: 0,
                                                borderBottomRightRadius: 0,
                                            },
                                            "& .MuiAccordionSummary-content": {
                                                alignItems: "center",
                                                margin: "12px 0",
                                                "&.Mui-expanded": {
                                                    margin: "12px 0",
                                                },
                                            },
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                width: "100%",
                                                gap: 2,
                                            }}
                                        >
                                            {/* Status Dot */}
                                            <Box
                                                sx={{
                                                    width: 12,
                                                    height: 12,
                                                    borderRadius: "50%",
                                                    backgroundColor: color,
                                                    mr: 2,
                                                    flexShrink: 0,
                                                }}
                                            />

                                            <Box sx={{ flex: 1 }}>
                                                <Typography
                                                    sx={{
                                                        fontSize: 14,
                                                        fontWeight: 500,
                                                        maxWidth: 200,
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        whiteSpace: "nowrap",
                                                    }}
                                                >
                                                    {file.label || "Uploaded File"}
                                                </Typography>

                                            </Box>


                                            {hasErrors && (
                                                <Tooltip title={`${failed} error(s)`}>
                                                    <ErrorOutlineOutlined sx={{ color: "#f44336" }} />
                                                </Tooltip>
                                            )}

                                            {/* Progress */}
                                            <Box
                                                sx={{
                                                    position: "relative",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 1,
                                                }}
                                            >

                                                <Box sx={{ position: "relative", width: 32, height: 32 }}>


                                                    <CircularProgress
                                                        variant="determinate"
                                                        value={100}
                                                        size={32}
                                                        thickness={5}
                                                        sx={{
                                                            color: "#e0e0e0",
                                                            position: "absolute",
                                                            left: 0,
                                                            top: 0,
                                                        }}
                                                    />

                                                    {/* Progress */}
                                                    <CircularProgress
                                                        variant="determinate"
                                                        value={percent}
                                                        size={32}
                                                        thickness={5}
                                                        sx={{ color }}

                                                    />
                                                </Box>

                                                <Typography variant="caption" sx={{ minWidth: 40 }}>
                                                    {/* {`${success}/${total}`} */}
                                                    {`${processed}/${total}`}
                                                </Typography>

                                                {/* ACTION BUTTONS */}
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 1,
                                                        minWidth: 40,
                                                        justifyContent: "center",
                                                    }}
                                                >
                                                    {/* Pause / Resume */}
                                                    {/* {percent < 100 ? ( */}
                                                    {percent < 100 && file?.generation?.status !== "COMPLETED" ? (
                                                        (() => {
                                                            const isPaused = file?.generation?.status === "PAUSED";

                                                            return (
                                                                <Tooltip
                                                                    title={isPaused ? "Resume" : "Pause"}
                                                                    placement="top"
                                                                    arrow
                                                                >
                                                                    <span>
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                onTogglePause(file);
                                                                            }}
                                                                            sx={{
                                                                                color,
                                                                                backgroundColor: alpha(color, 0.1),
                                                                                "&:hover": {
                                                                                    backgroundColor: alpha(color, 0.2),
                                                                                },
                                                                            }}
                                                                            disabled={actionLoading[file._id]?.pause}
                                                                        >
                                                                            {actionLoading[file._id]?.pause ? (
                                                                                <CircularProgress size={18} />
                                                                            ) : isPaused ? (
                                                                                <PlayArrow />
                                                                            ) : (
                                                                                <PauseCircleOutline />
                                                                            )}
                                                                        </IconButton>
                                                                    </span>
                                                                </Tooltip>
                                                            );
                                                        })()
                                                    ) : (

                                                        <Box sx={{ width: 32, height: 32 }} />
                                                    )}


                                                </Box>
                                            </Box>
                                        </Box>
                                    </AccordionSummary>

                                    {/* File Details */}
                                    <AccordionDetails
                                        sx={{ p: 2, pt: 0, borderTop: "1px solid #e0e0e0" }}
                                    >
                                        <Typography variant="h6" sx={{ mb: 1, mt: 1, fontWeight: 600 }}>
                                            File Details
                                        </Typography>

                                        <Grid container spacing={2}>

                                            <Grid item xs={12}>
                                                <Typography variant="body2" color="text.secondary">
                                                    File Name
                                                </Typography>
                                                <Typography variant="body2">{file.label || "-"}</Typography>
                                            </Grid>


                                            <Grid item xs={6}>
                                                <Typography variant="body2" color="text.secondary">
                                                    Owner
                                                </Typography>
                                                <Typography variant="body2">{file?.owner || "-"}</Typography>
                                            </Grid>


                                            <Grid item xs={6}>
                                                <Typography variant="body2" color="text.secondary">
                                                    Status
                                                </Typography>
                                                <Chip
                                                    label={file?.generation?.status}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: alpha(color, 0.1),
                                                        color: color,
                                                        fontWeight: 500,
                                                        fontSize: "12px",
                                                        width: "fit-content",
                                                    }}
                                                />
                                            </Grid>


                                            <Grid item xs={6}>
                                                <Typography variant="body2" color="text.secondary">
                                                    Created At
                                                </Typography>
                                                <Typography variant="body2">
                                                    {file?.createdAt
                                                        ? moment(file.createdAt).format("MM/DD/YYYY hh:mm A")
                                                        : "-"}
                                                </Typography>
                                            </Grid>


                                            <Grid item xs={6}>
                                                <Typography variant="body2" color="text.secondary">
                                                    Updated At
                                                </Typography>
                                                <Typography variant="body2">
                                                    {file?.updatedAt
                                                        ? moment(file.updatedAt).format("MM/DD/YYYY hh:mm A")
                                                        : "-"}
                                                </Typography>
                                            </Grid>
                                        </Grid>

                                        {/* STATS SECTION */}
                                        <Box mt={2}>
                                            <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                                                Processed Uploads
                                            </Typography>

                                            <Grid container spacing={2}>

                                                <Grid item xs={6}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Processed
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        {/* {file?.generation?.opState?.lastProcessedRow || 0} */}
                                                        {processed}
                                                    </Typography>
                                                </Grid>


                                                <Grid item xs={6}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Success
                                                    </Typography>
                                                    <Typography variant="body2" >
                                                        {file?.generation?.opState?.generated || 0}
                                                    </Typography>
                                                </Grid>


                                                <Grid item xs={6}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Failed
                                                    </Typography>
                                                    <Typography variant="body2" >
                                                        {file?.generation?.opState?.failed || 0}
                                                    </Typography>
                                                </Grid>


                                                <Grid item xs={6}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Duplicates
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        {file?.generation?.opState?.duplicates || 0}
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        </Box>
                                        {file?.generation?.opState?.failed > 0 && (
                                            <Accordion
                                                sx={{
                                                    mt: 2,
                                                    //border: "1px solid #f44336",
                                                    borderRadius: "8px !important",
                                                    "&:before": { display: "none" },
                                                }}
                                                className="glass-wrapper-25"
                                                onChange={(e, expanded) => {
                                                    if (expanded && !errorData[file._id]) {
                                                        loadErrors(file._id);
                                                    }
                                                }}
                                            >

                                                <AccordionSummary expandIcon={<ExpandMore />}>
                                                    <Box display="flex" alignItems="center" gap={1}>
                                                        <ErrorOutlineOutlined sx={{ color: "#f14f43" }} />
                                                        <Typography sx={{ fontWeight: 600, color: "#f14f43" }}>
                                                            File Errors Detected ({file?.generation?.opState?.failed || 0})
                                                        </Typography>
                                                    </Box>
                                                </AccordionSummary>

                                                {/* ERROR LIST */}

                                                <AccordionDetails
                                                    sx={{
                                                        maxHeight: 300,
                                                        overflowY: "auto",
                                                    }}
                                                    onScroll={(e) => {
                                                        const bottom =
                                                            e.target.scrollHeight - e.target.scrollTop <= e.target.clientHeight + 20;

                                                        if (
                                                            bottom &&
                                                            !errorLoading[file._id] &&
                                                            hasMoreErrors[file._id]
                                                        ) {
                                                            loadErrors(file._id, (errorPage[file._id] || 1) + 1);
                                                        }
                                                    }}
                                                >

                                                    {errorData[file._id]?.length > 0 ? (
                                                        <>
                                                            {errorData[file._id].map((err, i) => (
                                                                <Paper
                                                                    key={i}
                                                                    sx={{
                                                                        p: 1.5,
                                                                        mb: 1,
                                                                        // borderLeft: "4px solid #f44336",
                                                                    }}
                                                                    className="glass-pane"
                                                                >

                                                                    {/* <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                    {err?.data?.imap?.message ||
                                                                        err?.data?.smtp?.message ||
                                                                        err.message}
                                                                </Typography> */}
                                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                        {err?.data?.imap?.message?.includes("ENOTFOUND")
                                                                            ? "Invalid IMAP host. Please check the server address."
                                                                            : err?.data?.imap?.message ||
                                                                            err?.data?.smtp?.message ||
                                                                            err.message}
                                                                    </Typography>

                                                                    {(err.data?.email || err.timestamp) && (
                                                                        <Box
                                                                            sx={{
                                                                                display: "flex",
                                                                                justifyContent: "space-between",
                                                                                alignItems: "center",
                                                                                mt: 0.5
                                                                            }}
                                                                        >
                                                                            <Typography variant="caption" color="text.secondary">
                                                                                {err.data?.email || ""}
                                                                            </Typography>

                                                                            <Typography variant="caption" color="text.secondary">
                                                                                {err.timestamp
                                                                                    ? moment(err.timestamp).format("MM/DD/YYYY hh:mm A")
                                                                                    : ""}
                                                                            </Typography>
                                                                        </Box>
                                                                    )}
                                                                </Paper>
                                                            ))}

                                                            {errorLoading[file._id] && (
                                                                <Box display="flex" justifyContent="center" mt={1}>
                                                                    <CircularProgress size={18} />
                                                                </Box>
                                                            )}
                                                        </>
                                                    ) : errorLoading[file._id] ? (
                                                        <Box display="flex" justifyContent="center">
                                                            <CircularProgress size={20} />
                                                        </Box>
                                                    ) : (
                                                        <Typography variant="body2" color="text.secondary">
                                                            No errors found
                                                        </Typography>
                                                    )}
                                                </AccordionDetails>
                                            </Accordion>
                                        )}

                                    </AccordionDetails>
                                </Accordion>
                            );
                        })
                    )}
                </Box>
                {!loading && data.length > 0 && totalPages >= 1 && (
                    <Box
                        sx={{
                            borderTop: "0.5px solid #eee",
                            p: 1.5,
                            display: "flex",
                            justifyContent: "center",
                            backgroundColor: "#fff",
                            flexShrink: 0,
                        }}
                        className="glass-wrapper-50"
                    >
                        <Pagination
                            count={totalPages}
                            page={page}
                            onChange={(e, value) => onPageChange(value)}
                            size="small"
                            color="primary"
                        />
                    </Box>
                )}

            </Box>
        </Drawer>
    );
}